import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { ConsolidatedAccountData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  selectPortfolioDataLoadingStates,
  selectUniqueAccountsPortfolioData,
} from "@shared/slices/portfolio.slice";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useClientsData } from "@shared/utils/hooks/misc";
import {} from "@shared/common/constants";
import { ClientStatus } from "@shared/types";
import { DISPLAY_CURRENCIES } from "common/constants";
import { MOBILE_ALLOCATION_ID } from "testIds";
import FlatList from "common/Lists/FlatList";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import AccountsEmpty from "features/portfolio/Accounts/AccountsEmpty";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context";
import {
  getAccountsTotals,
  getUniqueAccounts,
} from "features/dashboard/common/utils";
import {
  PhoneAllocationCard,
  getTopShares,
  toSplitSegments,
  phoneMoney,
  usePhoneRestColor,
} from "features/dashboard/PhoneDashboard/PhoneSummary";
import {
  PhoneCardSlot,
  PhoneSectionAction,
  PhoneSectionHead,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import { Plus } from "lucide-react";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { getAccountExtraDetailsRoute } from "utils/routes";
import { useLocation } from "react-router-dom";
import NewLookAccountCard, {
  NewLookAccountCardSkeleton,
} from "features/portfolio/NewLook/NewLookAccountCard.tsx";

interface PhoneAccountsProps {
  searchFilter?: string;
  onCreateAccountClick: () => void;
}

type AccountItem = ConsolidatedAccountData & {
  key: string | number;
  client_status?: ClientStatus;
};
type ListItem = AccountItem | { key: string; skeleton: true };

const isSkeleton = (item: ListItem): item is { key: string; skeleton: true } =>
  "skeleton" in item;

/**
 * Phone accounts tab: the total balance in a hero card, then each account
 * as its own card (the desktop account card).
 */
const PhoneAccounts = ({
  searchFilter,
  onCreateAccountClick,
}: PhoneAccountsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const restColor = usePhoneRestColor();
  const { accounts, isLoading, isFetching, refetch, isLoadingPortfolioData } =
    useContext(AccountsPageContext)!;
  const location = useLocation();
  const { historyPush } = useHistoryWithQuery();
  const { loaded } = useSelector(selectPortfolioDataLoadingStates);
  const { notDeletedClientsData } = useClientsData();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isUsd = displayCurrency !== DISPLAY_CURRENCIES.BTC;
  const symbol = isUsd ? "$" : "₿";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  // The figures count each account once (linked accounts and repeated uuids
  // only once, as the desktop donut and the dashboard), while the list below
  // still shows every account.
  const uniqueAccountsData = useSelector(selectUniqueAccountsPortfolioData);
  const counted = useMemo(
    () => getUniqueAccounts(uniqueAccountsData),
    [uniqueAccountsData]
  );
  const shares = useMemo(
    () =>
      getTopShares(
        counted.map((account) => ({
          key: String(account.client_id),
          name: account.client,
          value: (isUsd ? account.total_in_usd : account.total_in_btc) || 0,
        })),
        4,
        t("phone.other"),
        restColor
      ),
    [counted, isUsd, restColor, t]
  );

  const total = useMemo(
    () => getAccountsTotals(counted, isUsd).total,
    [counted, isUsd]
  );
  const available = useMemo(
    () =>
      counted.reduce(
        (sum, account) =>
          sum +
          ((isUsd ? account.available_in_usd : account.available_in_btc) || 0),
        0
      ),
    [counted, isUsd]
  );
  // The accounts list arrives first; the figures wait for the portfolio data.
  const pending = (isLoading && !accounts.length) || isLoadingPortfolioData;

  const rows = useMemo(
    () =>
      accounts
        .filter((account) =>
          account.client
            .toLowerCase()
            .includes((searchFilter || "").toLowerCase())
        )
        .map((account) => ({ ...account, key: account.client_id })),
    [accounts, searchFilter]
  );

  const openAccountExtraDetails = (account: ConsolidatedAccountData) =>
    historyPush(
      getAccountExtraDetailsRoute(location.pathname, account.client_id)
    );

  const emptyState =
    loaded && accounts ? (
      <AccountsEmpty
        buttonOnClick={onCreateAccountClick}
        isSearch={!!searchFilter?.length}
        hasData={!!notDeletedClientsData?.length}
        filtersSlice={portfolioFilters}
        iconWidth={100}
      />
    ) : undefined;

  const items: ListItem[] =
    isLoading && !rows.length
      ? [0, 1, 2].map((index) => ({ key: `skeleton-${index}`, skeleton: true }))
      : rows;

  const showHero = pending || accounts.length > 0;

  const renderTop = () => (
    <>
      {showHero && (
        <PhoneAllocationCard
          testId={MOBILE_ALLOCATION_ID}
          label={t("phone.total_balance")}
          value={phoneMoney(total, symbol, precision)}
          loading={pending}
          meta={`${phoneMoney(available, symbol, precision)} ${t(
            "phone.available"
          ).toLowerCase()}`}
          segments={toSplitSegments(shares)}
        />
      )}
      {(pending || rows.length > 0) && (
        <PhoneSectionHead
          title={t("tabs.accounts")}
          extra={!pending && <Count>{rows.length}</Count>}
          trailing={
            // The FAB is gone on the new-look phone: this is the entry.
            <PhoneSectionAction type="button" onClick={onCreateAccountClick}>
              <Plus size={15} strokeWidth={2} />
              {t("phone.add_account")}
            </PhoneSectionAction>
          }
        />
      )}
    </>
  );

  return (
    <FlatList
      data={items}
      renderTopContainer={renderTop}
      renderContent={({ item }) => (
        <PhoneCardSlot>
          {isSkeleton(item) ? (
            <NewLookAccountCardSkeleton />
          ) : (
            <NewLookAccountCard
              account={item}
              loading={isLoadingPortfolioData}
              openAccountExtraDetails={openAccountExtraDetails}
            />
          )}
        </PhoneCardSlot>
      )}
      gap={0}
      paddingHorizontal={0}
      wrapperStyle={WRAPPER_STYLE}
      refreshing={isFetching}
      onRefresh={refetch}
      emptyState={emptyState}
    />
  );
};

export default PhoneAccounts;

const WRAPPER_STYLE = { padding: "0 0 8px" };

const Count = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
